import { DeleteOutline } from '@mui/icons-material';
import { styled, TextareaAutosize, useTheme } from '@mui/material';
import { Stack } from '@mui/system';
import React, { ChangeEvent, FC, useEffect, useRef, useState } from 'react';

interface EconsultClinicalReportCategorySectionProps {
  noteContent: string;
  onContentChanged: (value: string) => void;
  onDelete: () => void;
}

const StyledTextAreaAutoResize = styled(TextareaAutosize)(({ theme }) => ({
  '&::placeholder': {
    fontSize: '0.875rem',
    color: theme.palette.base.grey5
  },
  color: 'rgba(0, 0, 0, 0.87)',
  '&[aria-hidden="true"]': {
    height: '0 !important',
    minHeight: '0 !important',
    maxHeight: '0 !important',
    overflow: 'hidden !important',
    visibility: 'hidden',
    pointerEvents: 'none'
  }
}));

const EconsultClinicalReportCategorySection: FC<EconsultClinicalReportCategorySectionProps> = ({
  noteContent,
  onContentChanged,
  onDelete
}) => {
  const theme = useTheme();
  const [localNoteContent, setLocalNoteContent] = useState<string>(noteContent);
  const isFocusedRef = useRef<boolean>(false);
  const hasUserEditedWhileFocusedRef = useRef<boolean>(false);

  useEffect(() => {
    if (!isFocusedRef.current || !hasUserEditedWhileFocusedRef.current) {
      setLocalNoteContent(noteContent);
    }
  }, [noteContent]);

  const requestTextAreaStyle = {
    width: '100%',
    resize: 'vertical',
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.body2.fontSize,
    height: 180,
    overflow: 'auto',
    borderColor: theme.palette.base.grey5,
    paddingLeft: 5
  };

  const handleNoteContentChanged = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = event.target;
    hasUserEditedWhileFocusedRef.current = true;
    setLocalNoteContent(value);
    onContentChanged(value);
  };

  return (
    <Stack direction="row">
      <StyledTextAreaAutoResize
        onChange={handleNoteContentChanged}
        onFocus={() => {
          isFocusedRef.current = true;
          hasUserEditedWhileFocusedRef.current = false;
        }}
        onBlur={() => {
          isFocusedRef.current = false;
          hasUserEditedWhileFocusedRef.current = false;
          setLocalNoteContent(noteContent);
        }}
        maxRows={10}
        value={localNoteContent}
        style={requestTextAreaStyle}
        name="requestNte"
      />
      <DeleteOutline sx={{ color: 'primary.main' }} onClick={onDelete} />
    </Stack>
  );
};

export default EconsultClinicalReportCategorySection;
